import { useState, useEffect } from "react";
import { cn } from "@util/tailwind";
import { AlertTriangleIcon, CheckCircleIcon, LoaderIcon } from "lucide-react";

export type LoadingStatus = "idle" | "loading" | "success" | "error";

interface LoadingOverlayProps {
    status: LoadingStatus;
    message?: {
        loading?: string;
        success?: string;
        error?: string;
    };
    className?: string;
    autoHideDelay?: number;
    onAutoHide?: () => void;
}

export default function LoadingOverlay({
    status = "idle",
    message = {},
    className,
    autoHideDelay = 2000,
    onAutoHide,
}: LoadingOverlayProps) {
    const [visible, setVisible] = useState(false);
    const [activeStatus, setActiveStatus] = useState<LoadingStatus>("idle");

    useEffect(() => {
        // Show the overlay when status changes from idle
        if (status !== "idle") {
            setVisible(true);
            setActiveStatus(status);
        }

        // Auto-hide after success or error
        if ((status === "success" || status === "error") && autoHideDelay > 0) {
            const timer = setTimeout(() => {
                setVisible(false);
                if (onAutoHide) onAutoHide();
            }, autoHideDelay);

            return () => clearTimeout(timer);
        }

        // Reset to idle
        if (status === "idle") {
            setVisible(false);
            setActiveStatus("idle");
        }
    }, [status, autoHideDelay, onAutoHide]);

    if (!visible) return null;

    return (
        <div
            className={cn(
                "fixed inset-0 flex items-center justify-center z-50 bg-black/20 backdrop-blur-sm transition-all duration-300",
                className
            )}
        >
            <div
                className={cn(
                    "flex flex-col items-center p-8 rounded-lg shadow-lg transition-all duration-300 transform max-w-xs w-full bg-white"
                )}
            >
                <div
                    className={cn(
                        "rounded-full p-4 mb-4",
                        activeStatus === "loading" && "bg-blue-50",
                        activeStatus === "success" && "bg-green-50 border-2 border-green-500",
                        activeStatus === "error" && "bg-red-50 border-2 border-red-500",
                        className
                    )}
                    style={{ border: "0px solid rgb(34, 197, 94)" }}
                >
                    {activeStatus === "loading" && (
                        <LoaderIcon className="h-8 w-8 text-blue-600 animate-spin" />
                    )}
                    {activeStatus === "success" && (
                        <CheckCircleIcon className="h-8 w-8 text-green-500" />
                    )}
                    {activeStatus === "error" && (
                        <AlertTriangleIcon className="h-8 w-8 text-red-500" />
                    )}
                </div>

                <div className={cn("text-center", className)}>
                    <p
                        className={cn(
                            "text-lg font-medium mb-1",
                            activeStatus === "loading" && "text-gray-700",
                            activeStatus === "success" && "text-gray-800",
                            activeStatus === "error" && "text-gray-800"
                        )}
                    >
                        {activeStatus === "loading" && message.loading}
                        {activeStatus === "success" && message.success}
                        {activeStatus === "error" && message.error}
                    </p>

                    {activeStatus === "success" && (
                        <p className="text-sm text-gray-500 mb-4">
                            Your profile changes have been confirmed.
                        </p>
                    )}

                    {activeStatus === "error" && (
                        <p className="text-sm text-gray-500 mb-4">
                            Something went wrong.
                            <br />
                            Try again or contact support at support@ourteam.ai.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
